'use client';

import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';

import { appRoutes } from '@/appRoutes';
import {
  useVerifyMutation,
  useResendVerificationMutation,
} from '@/features/auth/api/authApi';
import { REGISTER_EMAIL_STORAGE_KEY } from '@/features/auth/constants';
import {
  resendVerificationSchema,
  verifySchema,
  type ResendVerificationSchema,
  type VerifySchema,
} from '@/features/auth/schemas/verifySchema';
import { MainButton, InlineButton, CodeInput } from '@web/ui';
import { getErrorMessage, notify, zodErrorsToFieldErrors } from '@web/utils';

import { useBootstrapAuth } from '../hooks';

import styles from './AuthForm.module.scss';

import type { ResendVerificationPayload } from '@/features/auth/types';

type VerifyErrors = Partial<Record<keyof VerifySchema, string>>;

const CODE_LENGTH = 6;

export function VerifyCodeForm() {
  const router = useRouter();
  const bootstrapAuth = useBootstrapAuth();
  const searchParams = useSearchParams();
  const [verify, verifyStatus] = useVerifyMutation();
  const [resendVerification, resendVerificationStatus] =
    useResendVerificationMutation();

  const urlEmail = (searchParams.get('email') ?? '').trim();
  const storedEmail =
    typeof window !== 'undefined'
      ? (sessionStorage.getItem(REGISTER_EMAIL_STORAGE_KEY)?.trim() ?? '')
      : '';

  const isEmailValid = Boolean(urlEmail) && storedEmail === urlEmail;

  const [errors, setErrors] = useState<VerifyErrors>({});
  const [code, setCode] = useState('');

  const email = urlEmail;

  useEffect(() => {
    if (!isEmailValid) {
      router.replace(appRoutes.auth.index);
    }
  }, [isEmailValid, router]);

  const handleCodeChange = useCallback((nextValue: string) => {
    setCode(nextValue);
    setErrors((prev) => ({ ...prev, code: undefined }));
  }, []);

  const submitVerification = useCallback(
    async (codeToSubmit: string) => {
      if (!email) {
        router.replace(appRoutes.auth.index);
        return;
      }

      const submission: VerifySchema = {
        email,
        code: codeToSubmit,
      };

      const parsed = verifySchema.safeParse(submission);

      if (!parsed.success) {
        setErrors(zodErrorsToFieldErrors<VerifySchema>(parsed.error));
        return;
      }

      try {
        const response = await verify(parsed.data).unwrap();

        setErrors({});

        notify({
          type: 'success',
          message:
            (response?.message as string) ??
            'Verification successful! Redirecting you now.',
        });

        if (typeof window !== 'undefined') {
          sessionStorage.removeItem(REGISTER_EMAIL_STORAGE_KEY);
        }

        const { user, workspace, is_workspace_setup_required } =
          response?.data ?? {};

        bootstrapAuth({
          currentUser: user ?? null,
          currentWorkspaceSlug: workspace?.slug ?? null,
          isWorkspaceSetupRequired: is_workspace_setup_required ?? false,
          isWorkspaceOnboarded: workspace?.is_workspace_onboarded ?? null,
        });

        router.push(appRoutes.home);
      } catch (error) {
        notify({ type: 'error', message: getErrorMessage(error) });
      } finally {
        setCode('');
      }
    },
    [email, router, verify, bootstrapAuth],
  );

  if (!isEmailValid) {
    return null;
  }

  const handleResendCode = async () => {
    try {
      const submission: ResendVerificationPayload = {
        email,
      };

      const parsed = resendVerificationSchema.safeParse(submission);

      if (!parsed.success) {
        setErrors(
          zodErrorsToFieldErrors<ResendVerificationSchema>(parsed.error),
        );
        return;
      }

      const response = await resendVerification(parsed.data).unwrap();

      notify({ type: 'success', message: response?.message as string });
    } catch (error) {
      notify({ type: 'error', message: getErrorMessage(error) });
    } finally {
      setCode('');
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    await submitVerification(code);
  };

  const isSubmitDisabled =
    verifyStatus.isLoading || !email || code.length !== CODE_LENGTH;

  return (
    <div className={styles.authContent}>
      <div className={styles.card}>
        <div className={styles.header}>
          <p className={styles.subtitle}>Step 2 of 2</p>
          <h1 className={styles.title}>Verify your email</h1>
          <p className={styles.subtitle}>
            Enter the 6-digit code we sent to <strong>{email}</strong>. This
            keeps your workspace secure.
          </p>
        </div>

        {errors.email && (
          <p className={styles.feedback} role="alert">
            {errors.email}
          </p>
        )}

        <form
          className={`${styles.form} ${styles.formActive}`}
          onSubmit={handleSubmit}
        >
          <div className={styles.codeInputContainer}>
            <CodeInput
              length={CODE_LENGTH}
              value={code}
              onChange={handleCodeChange}
              onComplete={(value) => {
                handleCodeChange(value);
              }}
              disabled={verifyStatus.isLoading}
              autoFocus
            />
          </div>

          {errors.code ? (
            <p className={styles.feedback} role="alert">
              {errors.code}
            </p>
          ) : null}

          <div className={styles.formActions}>
            <MainButton
              type="submit"
              disabled={isSubmitDisabled}
              isLoading={verifyStatus.isLoading}
              ariaLabel="Verify and continue"
            >
              Verify and continue
            </MainButton>

            <InlineButton
              isLoading={resendVerificationStatus.isLoading}
              onClick={handleResendCode}
              center
            >
              Resend code
            </InlineButton>

            <Link href={appRoutes.auth.index} className="inlineLink">
              Back to log in
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
